import React from "react";
import { Link } from "react-router-dom";
import { Alert, Modal, notification, Tooltip, Upload } from "antd";
import { PushpinFilled, QuestionCircleOutlined } from "@ant-design/icons";
import { CheckCircleFilled } from "@ant-design/icons";
import moment from "moment";

import { ERROR, INFO_APELLIDOS, INFO_TEL } from "./uiText";
import { DATE_FORMAT, DATE_FORMAT_EXP } from "./constantes";
import { setTabAspirante } from "../redux/aspirante/aspiranteDucks";

export const TEXTOS = {
  0: "No Aceptado",
  1: "Aceptado",
  2: "No se presentó",
};
export const COLOR = {
  2: "green",
  3: "red",
  4: "grey",
};

export const getTextCotejo = (id) => (TEXTOS[id] ? TEXTOS[id] : "");
export const getColorCotejo = (id) => (COLOR[id] ? COLOR[id] : "");

/**
 * Función para mostrar una notificación en la pantalla.
 * @param {string} type: success, info, warning o error
 * @param {string} message : Titulo a mostrar en pantalla
 * @param {string} description : Descripción del mensaje
 */
export const openNotification = (type, message, description) => {
  notification[type]({
    message: message,
    description: description,
  });
};

/**
 * Función que muestra el Modal tipo Success
 * @param {string} title: Titulo que se mostrará en el modal
 * @param {string} message: Mensaje que se mostrará en el contenido del modal
 * @param {string} url?: Url al que redireccionará el modal al dar clic en aceptar
 * @param {useHistory} history : useHistory o props.history
 */
export const openModalSucces = (title, message, url = null, history = null) => {
  Modal.success({
    title: title,
    okText: "Aceptar",
    keyboard: false,
    content: (
      <div>
        <p>{message}</p>
      </div>
    ),
    onOk() {
      url && history.push(url);
    },
  });
};

/**
 * Función que muestra el Modal tipo Error.
 * @param {string} title: Titulo que se mostrará en el modal
 * @param {string} message: Mensaje que se mostrará en el contenido del modal
 */
export const openModalError = (title, message) => {
  Modal.error({
    title: title,
    okText: "Aceptar",
    keyboard: false,
    content: message,
  });
};

/**
 * Función que elimina los espacios de los value del json object
 * @param {json} obj
 * @returns Json object
 */
export const trimObjectJson = (obj) => {
  let trimmed = JSON.stringify(obj, (_, value) => {
    if (typeof value === "string") {
      return value.trim();
    }
    return value;
  });
  return JSON.parse(trimmed);
};
/**
 * Función para asignarle un formato especifico a la fecha
 * @param {Date} fecha
 * @param {string} DATE_FORMAT
 * @returns fecha con el formato establecido
 */
export const formatFecha = (fecha, DATE_FORMAT) => {
  return moment(fecha).format(DATE_FORMAT);
};

/**
 *
 * @param {string} texto
 * @returns texto con con primera letra myuscula de cada palabra
 */
export const titleCase = (texto) => {
  const re =
    /(^|[^A-Za-zÁÉÍÓÚÜÑáéíóúüñ])(?:([a-záéíóúüñ])|([A-ZÁÉÍÓÚÜÑ]))|([A-ZÁÉÍÓÚÜÑ]+)/gu;
  return texto.replace(
    re,
    (m, caracterPrevio, minuscInicial, mayuscInicial, mayuscIntermedias) => {
      const locale = ["es", "gl", "ca", "pt", "en"];
      //Son letras mayúsculas en el medio de la palabra
      // => llevar a minúsculas.
      if (mayuscIntermedias) return mayuscIntermedias.toLocaleLowerCase(locale);
      //Es la letra inicial de la palabra
      // => dejar el caracter previo como está.
      // => si la primera letra es minúscula, capitalizar
      //    sino, dejar como está.
      return (
        caracterPrevio +
        (minuscInicial
          ? minuscInicial.toLocaleUpperCase(locale)
          : mayuscInicial)
      );
    }
  );
};

/**
 * Función que convierte el texto en mayúsculas.
 * @param {Event} e
 * @returns value toUpperCase
 */
export const valueUpperCase = (e) => {
  return e.target.value.toUpperCase();
};

/**
 * Función que convierte el texto en minúsculas.
 * @param {Event} e
 * @returns value toLowerCase
 */
export const valueLowerCase = (e) => {
  return e.target.value.toLowerCase();
};

/**
 * Función que obtiene la edad a partir de la fecha de nacimiento
 * @param {string} nacimiento : Fecha de nacimiento, Formato de la fecha "YYYY/MM/DD"
 * @returns La edad
 */
export const getEdad = (nacimiento) => {
  let hoy = new Date();
  let fechaNacimiento = new Date(nacimiento);
  let edad = hoy.getFullYear() - fechaNacimiento.getFullYear();
  let diferenciaMeses = hoy.getMonth() - fechaNacimiento.getMonth();
  if (
    diferenciaMeses < 0 ||
    (diferenciaMeses === 0 && hoy.getDate() < fechaNacimiento.getDate())
  ) {
    edad--;
  }
  return edad;
};

/**
 * Función que recupera la fecha de nacimiento de acuerdo a la clave de elector
 * @param {string} claveElector
 * @returns fecha de nacimiento
 */
export const getFechaNacimiento = (claveElector) => {
  const anio = claveElector.slice(6, 8);
  const mes = claveElector.slice(8, 10);
  const dia = claveElector.slice(10, 12);
  let fecha;
  // empleando la regla de fechas de microsoft excel, last update: 2020
  if (parseInt(anio) >= 0 && parseInt(anio) <= 29) {
    // fecha = `20${anio}-${mes}-${dia}`;
    fecha = `${dia}-${mes}-20${anio}-`;
  }
  if (parseInt(anio) >= 30 && parseInt(anio) <= 99) {
    fecha = `${dia}-${mes}-19${anio}-`;
  }
  return moment(fecha, DATE_FORMAT);
};

/**
 * Obtiene la fecha de nacimiento a partir del curp
 * @param {string} curp
 * @returns moment fecha nacimiento
 */
export const getFecha = (curp) => {
  let m = curp.match(/^\w{4}(\w{2})(\w{2})(\w{2})(\w{1})(\w{2})/);
  let fecha;
  //Fecha de nacimiento
  let anio = parseInt(m[1], 10);
  let mes = parseInt(m[2], 10);
  let dia = parseInt(m[3], 10);
  // empleando la regla de fechas de microsoft excel, last update: 2020
  if (parseInt(anio) >= 0 && parseInt(anio) <= 29) {
    // fecha = `20${anio}-${mes}-${dia}`;
    anio = anio < 10 ? `0${anio}` : anio;
    fecha = `${dia}-${mes}-20${anio}`;
  }
  if (parseInt(anio) >= 30 && parseInt(anio) <= 99) {
    fecha = `${dia}-${mes}-19${anio}`;
  }
  return moment(fecha, DATE_FORMAT);
};

/**
 * Función que obtiene el sexo a partir de la clave de elector
 * @param {string} claveElector
 * @returns
 */
export const getSexo = (claveElector) => {
  if (claveElector.slice(14, 15) === "M") {
    return "M";
  }
  return "H";
};

/**
 * Función para bloquear los días siguientes del DatePicker
 */
export const disabledDate = (current) => {
  return current && current > moment().endOf("day");
};
/**
 * Función para bloquear los días siguientes del DatePicker y regresar la fecha 18 años antes
 */
export const disabledDate18 = (current) => {
  return moment().add(-18, "years") <= current;
};

/**
 * Component para mostrar el label en los inputs que son requeridos
 * @param {string} label: Label del input
 * @param {boolean} isConsulta: Si se mostrará en modo consulta
 * @param {boolean} isRequired: Si el input es requerido.
 * @returns JSX.Element
 */
export const Label = ({ label, isConsulta, isRequired, isSensible }) => {
  return (
    <>
      {!isConsulta && isRequired && <Ast />}
      <label className={isConsulta ? "texto-normal-consulta" : "texto-normal"}>
        {label} {isConsulta && ":"}
        {isSensible && <DatoSensible size={20} />}
      </label>
    </>
  );
};

/**
 * Component que muestra * de requerido
 * @returns JSX.Element
 */
export const Ast = () => {
  return <label className="dom-rojo">*</label>;
};

/**
 * Componente que muestra un tooltip con imágen.
 * @param {JSX.Element | string} children: Texto o componente que se mostrará junto con el tooltip
 * @param {image} img: Imagen a mostrar dentro del tooltip
 * @param {string} alt: Alt de la imagen
 * @param {string} placement: Posición en la que se mostrará el tooltip, en caso de no especificar por defecto será top.
 * @param {string} estilo:Estilos de la imagen
 * @returns JSX.Element
 */
export const TooltipImg = (props) => {
  const { children, img, text, alt, placement = "top", estilo } = props;
  return (
    <Tooltip
      title={
        img ? (
          <img src={img} alt={alt} className={estilo ? estilo : ""} />
        ) : (
          text
        )
      }
      placement={placement}
      overlayStyle={{ maxWidth: 800 }}
    >
      {children} <QuestionCircleOutlined />
    </Tooltip>
  );
};

export const DatoSensible = ({ size = 25 }) => {
  return <PushpinFilled style={{ fontSize: size }} rotate={280} />;
};

/**
 * Función que Muestra un tipo de Alert.
 * @param {string} type: Tipo de Alert success, info, warning, error.
 * @param {string} message: Mensaje a mostrar
 * @param {object} Icon: Icono a mostrar
 * @returns JSX.Element
 */
export const AlertGeneric = ({ type, message, Icon }) => {
  return (
    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
      <Alert message={message} type={type} icon={Icon && <Icon />} showIcon />
    </div>
  );
};

export const blobToFile = (file) => {
  let fileBlob = new Blob([file.originFileObj], {
    type: file.originFileObj.type,
  });
  fileBlob.name = "copy-of-" + file.originFileObj.name;
  fileBlob.lastModifiedDate = file.originFileObj.lastModifiedDate;

  const fileCopy = new File([fileBlob], file.name, {
    lastModified: new Date().getTime(),
    type: file.type,
  });

  return fileCopy;
};

export const validarFechas = (fechaInicio, fechaFin) => {
  return fechaInicio > fechaFin;
};

/**
 * Función para convertir un archivo en base64 a un
 * @param {string} base64Data
 * @param {string} contentType
 * @returns Blob
 */
export const base64toBlob = (base64Data, contentType = "application/pdf") => {
  contentType = contentType || "";
  let sliceSize = 1024;
  let byteCharacters = atob(base64Data);
  let bytesLength = byteCharacters.length;
  let slicesCount = Math.ceil(bytesLength / sliceSize);
  let byteArrays = new Array(slicesCount);

  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    let begin = sliceIndex * sliceSize;
    let end = Math.min(begin + sliceSize, bytesLength);

    let bytes = new Array(end - begin);
    for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }
  return new Blob(byteArrays, { type: contentType });
};

//-------- MÓDULO DE REGISTRO ASPIRANTE --------------------------------------------
export const validaInfoPersonal = (jsonForm) => {
  const { apePaterno, apeMaterno, telParticular, telOficina, telMovil } =
    jsonForm;

  if (!apePaterno && !apeMaterno) {
    openNotification("error", ERROR, INFO_APELLIDOS);
    return false;
  }

  if (!telParticular && !telOficina && !telMovil) {
    openNotification("error", ERROR, INFO_TEL);
    return false;
  }
  return true;
};

/**
 * Función que muestra un mensaje de confirmación y permite avanzar a cualquier sección del Tab.
 * @param {string} titulo: Que mostrará el modal
 * @param {string} mensaje: El contenido que se mpstrará en el modal.
 * @param {string} tab: El número del tab al cual se va a posicionar
 * @param {Function} dispatch: El hook del dispatch para poder cambiar el state de redux
 */
export const nextTabConfirm = (titulo, mensaje, tab, dispatch) => {
  Modal.confirm({
    title: titulo,
    icon: <CheckCircleFilled style={{ color: "#d5007f" }} />,
    okText: "Continuar",
    // okButtonProps: {
    //   disabled: tab >= 4,//Provisional borar cuando se libere el sprint 6
    // },
    cancelText: "Permanecer",
    cancelButtonProps: {
      type: "ghost",
    },
    keyboard: false,
    content: <div className="justificar">{mensaje}</div>,
    onOk() {
      if (tab >= 5) {
        window.location.href = "#/admin/inicio";
      } else {
        dispatch(setTabAspirante(tab));
      }
    },
  });
};

/**
 * Función que valida el pes y el nombre de un documento pdf.
 * @param {File} file
 * @returns boolean si es valido el documento
 */
export const validaArchivosPDF = (file, size = 5) => {
  const isPdf = file.type === "application/pdf";
  if (!isPdf) {
    openNotification("error", ERROR, "Solo archivos en formato pdf");
    return false;
  }
  const isLt2M = file.size / 1024 / 1024 < size;
  if (!isLt2M || isLt2M === 0) {
    openNotification("error", ERROR, `¡Peso máximo de ${size}MB!`);
    return false;
  }
  const nameFile = validateFileNamePDF(file.name);
  if (!nameFile) {
    const message = `El nombre de archivo ${file.name} es inválido. El nombre del documento no debe contener caracteres especiales.`;
    openNotification("error", ERROR, message);
    return false;
  }
  return isPdf && isLt2M && nameFile;
};
export const files = {
  multiple: false,
  showUploadList: {
    showDownloadIcon: false,
    showRemoveIcon: false,
    removeIcon: false,
    showPreviewIcon: false,
  },
  //Valida el archivo antes de cargarlo
  beforeUpload: (file) => {
    if (!validaArchivosPDF(file)) {
      return Upload.LIST_IGNORE;
    }
  },
};
export const files_max = {
  multiple: false,
  showUploadList: {
    showDownloadIcon: false,
    showRemoveIcon: false,
    removeIcon: false,
    showPreviewIcon: false,
  },
  //Valida el archivo antes de cargarlo
  beforeUpload: (file) => {
    if (!validaArchivosPDF(file, 15)) {
      return Upload.LIST_IGNORE;
    }
  },
};
/**
 * Valida que que no tenga caracteres especiales.
 */
export const validateFileNamePDF = (e) => {
  let patron = /^[0-9a-z-äëïöüÄËÏÖÜáéíóúÁÉÍÓÚÑñA-Z_-\s]+.[pdf/PDF]+$/;
  return e.match(patron);
};

export const bloqueoFechas = (fechaInicio, fechaFin) => {
  const hoy = new Date(moment().format(DATE_FORMAT_EXP));
  const startDate = new Date(moment(fechaInicio).format(DATE_FORMAT_EXP));
  const endDate = new Date(moment(fechaFin).format(DATE_FORMAT_EXP));

  //Si la fecha actual es menor a la fecha inicio se bloquea el botón
  if (hoy < startDate) return true;
  //Si la fecha actual es igual o mayor a la fecha inicio y no existe fecha fin se habilita el botón
  if (hoy >= startDate && isNaN(endDate.getTime())) {
    return false;
  }
  //Si la fecha actual se encuentra en el rango de las fechas inicio y fin se habilita el botón
  //Si ya pasaron las fechas y el estatus se encuentra en 1 solo se bloquea el botón
  return hoy >= startDate && hoy <= endDate ? false : true;
};

//Home publico
export const archivosApoyo = [
  { id: 1, texto: "Difusión del proceso (video)", url: "" },
  { id: 2, texto: "Infografía", url: "" },
  { id: 3, texto: "Manuales", url: "" },
  { id: 4, texto: "Ver material de apoyo", url: "" },
];
export const archivosRegistrarse = [
  {
    id: 1,
    texto: "RFC con homoclave",
    url: "/RFCexplica.svg",
    ejemplo: (
      <Link to={""} className="link">
        Ver ejemplo
      </Link>
    ),
  },
  {
    id: 2,
    texto: "CURP",
    url: "",
    ejemplo: (
      <a
        className="link"
        href="https://www.gob.mx/curp/"
        target="_blank"
        rel="noopener noreferrer"
      >
        <u> Ir a consultar</u>
      </a>
    ),
  },
  {
    id: 3,
    texto: "Clave de elector o Comprobante de trámite",
    url: "/FUAR.png",
    ejemplo: (
      <Link to={""} className="link">
        ¿Qué es esto?
      </Link>
    ),
  },
  { id: 4, texto: "Cuenta de correo electrónico", url: "", ejemplo: "" },
  {
    id: 5,
    texto:
      "Certificado de educación media superior, Título o cédula profesional, en formato PDF",
    url: "",
    ejemplo: "",
  },
];
